import React, { useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Button,
    Chip,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider
} from '@mui/material';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import { TokenResponse } from '@react-oauth/google';

import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useUser } from '../../context/UserContext';

// --- КОНФИГУРАЦИЯ ПАКЕТОВ КРЕДИТОВ ---
// здесь поставь любые финальные цифры, пример:
const CREDIT_PACKS = [
    {
        id: 'starter',
        credits: 40,
        price: 9,
        title: 'Starter',
        description: 'Try EcomAI on a few products.',
        saveLabel: '',
        pricePerCredit: '€0.23'
    },
    {
        id: 'growth',
        credits: 150,
        price: 25,
        title: 'Growth',
        description: 'Best value for growing stores.',
        saveLabel: 'Save 25%',
        pricePerCredit: '€0.17'
    },
    {
        id: 'business',
        credits: 450,
        price: 59,
        title: 'Power Seller',
        description: 'For high volume and multiple stores.',
        saveLabel: 'Save 45%',
        pricePerCredit: '€0.13'
    }
];

// --- ЧТО СТОИТ 1 КРЕДИТ ---
const CREDIT_USAGE = [
    {
        icon: <DescriptionIcon fontSize="large" color="primary" />,
        title: 'Research',
        cost: '1 Credit',
        desc: 'Deep market analysis, SEO keywords, title & description generation in one go.'
    },
    {
        icon: <ImageIcon fontSize="large" color="secondary" />,
        title: 'Photos',
        cost: '2 Credits',
        desc: 'Generates high-quality product photography or lifestyle background.'
    },
    {
        icon: <VideoLibraryIcon fontSize="large" sx={{ color: '#FFD700' }} />,
        title: 'Videos',
        cost: '5 Credits',
        desc: 'Creates a short promotional video based on your product.'
    }
];

const FAQS = [
    {
        q: 'Do credits expire?',
        a: 'No! Your credits act like a prepaid balance. They remain in your account until you use them.'
    },
    {
        q: 'What happens if I run out?',
        a: 'You can simply purchase another pack. We will also notify you when your balance is low.'
    },
    {
        q: 'Can I get a refund?',
        a: 'We offer a 14-day refund policy for unused credit packs.'
    }
];

const PricingPage = () => {
    const { login, isLoading } = useUser();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleLoginSuccess = async (tokenResponse: TokenResponse) => {
        try {
            await login(tokenResponse);
        } catch (loginError) {
            console.error(loginError);
        }
    };

    const handlePurchase = (packId: string) => {
        console.log(`Buying pack: ${packId}`);
        // TODO: интеграция с Paddle / Stripe
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: (theme) =>
                    `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Header onLoginSuccess={handleLoginSuccess} isLoading={isLoading} />

            <Container component="main" maxWidth="lg" sx={{ flexGrow: 1, py: 8 }}>
                {/* HERO */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography variant="h2" component="h1" sx={{ fontWeight: 800, mb: 2 }}>
                        Pay As You Go.{' '}
                        <Box component="span" sx={{ color: 'primary.main' }}>
                            No Subscriptions.
                        </Box>
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                        Purchase credits once and use them whenever you need. <br />
                        Credits never expire.
                    </Typography>
                </Box>

                {/* PACKAGES */}
                <Grid container spacing={4} alignItems="flex-end" justifyContent="center" sx={{ mb: 10 }}>
                    {CREDIT_PACKS.map((pack) => (
                        <Grid item key={pack.id} xs={12} md={4}>
                            <Paper
                                elevation={4}
                                sx={{
                                    p: 4,
                                    borderRadius: 4,
                                    position: 'relative',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid',
                                    borderColor: 'rgba(255,255,255,0.1)',
                                    backgroundColor: 'rgba(30, 30, 30, 0.4)',
                                    backdropFilter: 'blur(20px)',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                {pack.saveLabel && (
                                    <Chip
                                        label={pack.saveLabel}
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            top: -16,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontWeight: 'bold',
                                            px: 2
                                        }}
                                    />
                                )}

                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    <Typography variant="overline" color="text.secondary" letterSpacing={2}>
                                        {pack.title}
                                    </Typography>

                                    <Typography
                                        variant="h3"
                                        component="div"
                                        fontWeight="800"
                                        color="primary.main"
                                        sx={{ my: 1 }}
                                    >
                                        {pack.credits}
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Credits
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'inline-block',
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 2,
                                            mt: 1
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {pack.pricePerCredit} / credit
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                {/* Можно добавить фичи позже, когда сформулируешь оффер */}
                                {/* <Stack spacing={2} sx={{ mb: 4 }}>
                                    ...
                                </Stack> */}

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    onClick={() => handlePurchase(pack.id)}
                                    sx={{ height: 50, fontSize: '1.1rem' }}
                                >
                                    Buy for €{pack.price}
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* WHAT IS A CREDIT? SECTION */}
                <Typography variant="h4" textAlign="center" fontWeight="bold" mb={6}>
                    What can you do with credits?
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {CREDIT_USAGE.map((usage) => (
                        <Grid item xs={12} md={4} key={usage.title}>
                            <Paper
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    height: '100%',
                                    background: 'rgba(255,255,255,0.02)',
                                    border: '1px solid rgba(255,255,255,0.05)'
                                }}
                            >
                                <Box sx={{ mb: 2 }}>{usage.icon}</Box>
                                <Typography variant="h6" gutterBottom>
                                    {usage.title}
                                </Typography>
                                <Typography variant="h5" color="primary.main" fontWeight="bold" gutterBottom>
                                    {usage.cost}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {usage.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* FAQ */}
                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography variant="h5" textAlign="center" fontWeight="bold" mb={4}>
                        Common Questions
                    </Typography>
                    {FAQS.map((item, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                mb: 1,
                                background: 'transparent',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px !important',
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight="600">{item.q}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">{item.a}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Container>

            <Footer />
        </Box>
    );
};

export default PricingPage;
